import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Instagram, MessageCircle } from 'lucide-react';
import wcImg from '../wc2026.jpg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HomePage() {
  const navigate = useNavigate();
  const [popularProducts, setPopularProducts] = useState([]);
  const [offerProducts, setOfferProducts] = useState([]);
  const [discountProducts, setDiscountProducts] = useState([]);

  useEffect(() => {
    // Fetch products for the showcase
    const fetchData = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/products`);
        if (response.ok) {
          const data = await response.json();
          // Novedades (Prioridad manual, si no hay manuales, mostrar últimos 8)
          let novedades = data.filter(p => p.novedad === true).sort((a, b) => new Date(b.fecha_ingreso) - new Date(a.fecha_ingreso)).slice(0, 8);
          
          setPopularProducts(novedades);
          // Products on offer
          const offers = data.filter(p => p.oferta === true).slice(0, 8);
          setOfferProducts(offers);

          // Products with discount
          const discounts = data.filter(p => p.descuento > 0).slice(0, 8);
          setDiscountProducts(discounts);
        }
      } catch (e) {
        console.error("Error loading products", e);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="home-container bg-[#0A0A0A] text-white min-h-screen">
      
      {/* 
        ======================================================================
        1. MAIN HERO BANNER (Selecciones / Mundial) - Full Screen Edge-to-Edge
        ======================================================================
      */}
      <section className="relative h-[85vh] w-full overflow-hidden flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-16 group">
        {/* Foto de fondo Mundialista Local */}
        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[20s] group-hover:scale-105" style={{ backgroundImage: `url(${wcImg})` }}></div>
        
        {/* Sombras y gradientes oscuros */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>

        <div className="relative z-20 w-full max-w-7xl mx-auto flex flex-col items-start mt-10">
          {/* Logo Estilo Catalogo */}
          <div className="flex items-center gap-4 mb-6">
            <div className="p-1 bg-[#39FF14] rounded-full shadow-[0_0_15px_#39FF14] animate-pulse">
              <img src="/fu-logo.png" alt="Logo" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-contain bg-black p-1" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-5xl font-black text-white uppercase tracking-tighter leading-none italic drop-shadow-lg">
                INVERSIONES<span className="text-[#39FF14] inline-block">FU</span>
              </h1>
              <div className="h-1 w-16 bg-[#39FF14] mt-1 shadow-[0_0_10px_#39FF14]"></div>
            </div>
          </div>

          <div className="bg-[#39FF14]/20 border border-[#39FF14] text-[#39FF14] px-4 py-1 rounded-full text-[10px] md:text-xs font-bold tracking-widest uppercase mb-4 backdrop-blur-md">
            Rumbo al Mundial 2026
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black italic uppercase leading-[0.9] mb-4 drop-shadow-2xl">
            REPRESENTA A <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-300 to-gray-500">TU SELECCIÓN</span>
          </h2>
          <p className="text-sm sm:text-base md:text-xl text-gray-300 font-medium mb-8 max-w-2xl drop-shadow-md">
            Equípate con lo mejor para vivir la pasión del fútbol. Las nuevas camisetas de selecciones nacionales ya aterrizaron en el catálogo con calidad premium garantizada.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-8">
            <button 
              onClick={() => navigate('/catalogo?cat=sports')}
              className="w-full sm:w-auto bg-white text-black font-black uppercase tracking-widest text-sm md:text-base px-6 py-3 md:px-12 md:py-4 rounded-full hover:bg-gray-200 transition-all hover:scale-105 shadow-[0_10px_30px_rgba(255,255,255,0.2)]"
            >
              Comprar Ahora
            </button>
            <button 
              onClick={() => navigate('/catalogo')}
              className="w-full sm:w-auto bg-black/30 backdrop-blur-md text-white border-2 border-white font-black uppercase tracking-widest text-sm md:text-base px-6 py-3 md:px-12 md:py-4 rounded-full hover:bg-white hover:text-black transition-all hover:scale-105"
            >
              Explorar Catálogo
            </button>
          </div>

          {/* Redes Sociales Oficiales */}
          <div className="flex gap-4">
            <a href="https://www.instagram.com/inversionesfu.trujillo" target="_blank" rel="noreferrer" className="bg-black/50 p-3 rounded-full border border-gray-800 text-white hover:text-[#E1306C] hover:border-[#E1306C] transition-all hover:scale-110 shadow-lg">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://www.tiktok.com/@inversionesfu.trujillo" target="_blank" rel="noreferrer" className="bg-black/50 p-3 rounded-full border border-gray-800 text-white hover:text-[#ff0050] hover:border-[#ff0050] transition-all hover:scale-110 shadow-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.17-2.89-.6-4.13-1.41-.21-.14-.41-.3-.6-.47l-.02 5.92c-.01 1.63-.52 3.25-1.55 4.52-1.25 1.56-3.18 2.52-5.18 2.62-2 .11-4.08-.54-5.59-1.89-1.61-1.43-2.52-3.62-2.39-5.77.12-2.02 1.25-3.95 2.97-5.06 1.34-.87 2.96-1.25 4.54-1.12 0 1.5.01 3 .01 4.5l-.26-.01c-1.34 0-2.7.74-3.32 1.94-.36.68-.45 1.5-.27 2.25.26.96 1 1.79 1.95 2.11.75.26 1.59.18 2.3-.18 1.1-.55 1.8-1.78 1.8-3.02l.01-12.87V.02z" />
              </svg>
            </a>
            <a href="https://wa.me/584247329866" target="_blank" rel="noreferrer" className="bg-black/50 p-3 rounded-full border border-gray-800 text-white hover:text-[#25D366] hover:border-[#25D366] transition-all hover:scale-110 shadow-lg">
              <MessageCircle className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* 
        ======================================================================
        2. GRID DE CATEGORÍAS TIPO MOSAICO (Nike Style)
        ======================================================================
      */}
      <section className="py-8 w-full max-w-[1920px] mx-auto">
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <button 
            onClick={() => navigate('/catalogo?cat=oferta')}
            className="px-8 py-4 bg-[#FF003C] text-white font-black rounded-full hover:scale-110 transition-all shadow-[0_0_20px_rgba(255,0,60,0.5)] uppercase italic tracking-tighter"
          >
            🔥 Ver Ofertas
          </button>
          <button 
            onClick={() => navigate('/catalogo?cat=descuento')}
            className="px-8 py-4 bg-[#39FF14] text-black font-black rounded-full hover:scale-110 transition-all shadow-[0_0_20px_rgba(57,255,20,0.5)] uppercase italic tracking-tighter"
          >
            🏷️ Ver Descuentos %
          </button>
        </div>

        {/* Ocultamos el título simple y dejamos que las fotos hablen por sí solas, igual que Nike */}
        
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-1 md:gap-2 px-1 md:px-2 h-auto md:h-[65vh]">
          
          {/* Tarjeta Fútbol */}
          <div 
            onClick={() => navigate('/catalogo?cat=sports')} 
            className="relative aspect-[3/4] md:aspect-auto md:h-full group cursor-pointer overflow-hidden border border-gray-900 bg-black"
          >
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=1200')] bg-cover bg-center transition-transform duration-[10s] ease-out group-hover:scale-110 opacity-80 group-hover:opacity-100"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute bottom-6 left-6">
              <span className="bg-white text-black font-black px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:bg-gray-200 transition-colors inline-block shadow-lg">Fútbol</span>
            </div>
          </div>

          {/* Tarjeta Otros Deportes */}
          <div 
            onClick={() => navigate('/catalogo?cat=leagues')} 
            className="relative aspect-[3/4] md:aspect-auto md:h-full group cursor-pointer overflow-hidden border border-gray-900 bg-black"
          >
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1504450758481-7338eba7524a?q=80&w=1200')] bg-cover bg-center transition-transform duration-[10s] ease-out group-hover:scale-110 opacity-80 group-hover:opacity-100"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute bottom-6 left-6">
              <span className="bg-white text-black font-black px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:bg-gray-200 transition-colors inline-block shadow-lg">Otros Deportes</span>
            </div>
          </div>

          {/* Tarjeta Urbano */}
          <div 
            onClick={() => navigate('/catalogo?cat=urban')} 
            className="relative aspect-[3/4] md:aspect-auto md:h-full group cursor-pointer overflow-hidden border border-gray-900 bg-black"
          >
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=1200')] bg-cover bg-center transition-transform duration-[10s] ease-out group-hover:scale-110 opacity-80 group-hover:opacity-100"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute bottom-6 left-6">
              <span className="bg-white text-black font-black px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:bg-gray-200 transition-colors inline-block shadow-lg">Streetwear</span>
            </div>
          </div>

          {/* Tarjeta Gorras */}
          <div 
            onClick={() => navigate('/catalogo?cat=caps')} 
            className="relative aspect-[3/4] md:aspect-auto md:h-full group cursor-pointer overflow-hidden border border-gray-900 bg-black"
          >
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1588850561407-ed78c282e89b?q=80&w=1200')] bg-cover bg-center transition-transform duration-[10s] ease-out group-hover:scale-110 opacity-80 group-hover:opacity-100"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute bottom-6 left-6">
              <span className="bg-white text-black font-black px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:bg-gray-200 transition-colors inline-block shadow-lg">Gorras</span>
            </div>
          </div>

          {/* Tarjeta Frío */}
          <div 
            onClick={() => navigate('/catalogo?cat=winter')} 
            className="relative aspect-[3/4] md:aspect-auto md:h-full group cursor-pointer overflow-hidden border border-gray-900 bg-black"
          >
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1478719059408-592965723cbc?q=80&w=1200')] bg-cover bg-center transition-transform duration-[10s] ease-out group-hover:scale-110 opacity-80 group-hover:opacity-100"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute bottom-6 left-6">
              <span className="bg-white text-black font-black px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:bg-gray-200 transition-colors inline-block shadow-lg">Frío</span>
            </div>
          </div>

        </div>
      </section>

      {/* 
        ======================================================================
        3. SPLIT SCREEN 50/50 (Promocional y Tienda Física)
        ======================================================================
      */}
      <section className="w-full mt-2 md:mt-4 mb-20 px-1 md:px-2 max-w-[1920px] mx-auto">
        <div className="flex flex-col lg:flex-row h-auto lg:h-[70vh] gap-1 md:gap-2">
          
          {/* Bloque Izquierdo: Tienda Física */}
          <div className="relative w-full lg:w-1/2 aspect-square md:aspect-auto md:h-[50vh] lg:h-full overflow-hidden group border border-gray-900 bg-black">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1556906781-9a412961c28c?q=80&w=1600')] bg-cover bg-center transition-transform duration-[15s] group-hover:scale-110 opacity-70 group-hover:opacity-100"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 to-transparent"></div>
            
            <div className="relative z-10 w-full h-full flex flex-col justify-end p-8 md:p-14">
              <p className="text-[#39FF14] font-black tracking-[0.2em] text-xs uppercase mb-3 drop-shadow-md">Visítanos</p>
              <h2 className="text-4xl md:text-5xl font-black italic uppercase leading-[0.9] mb-4">Nuestra Casa.<br/>Tus Reglas.</h2>
              <p className="text-gray-300 text-sm md:text-base font-medium mb-8 max-w-sm">
                Conoce el lado premium de Valera. Pruébate nuestra ropa y zapatillas directamente en el <strong className="text-white">CC Edivica II, Piso 1, Local 26.</strong>
              </p>
              <div>
                <button 
                  onClick={() => window.open('https://wa.me/584247329866', '_blank')}
                  className="bg-white text-black font-black rounded-full px-8 py-3 uppercase tracking-widest hover:scale-105 transition-transform text-sm shadow-xl"
                >
                  Contáctanos
                </button>
              </div>
            </div>
          </div>

          {/* Bloque Derecho: Atención Segura */}
          <div className="relative w-full lg:w-1/2 aspect-square md:aspect-auto md:h-[50vh] lg:h-full overflow-hidden group border border-gray-900 bg-black">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?q=80&w=1600')] bg-cover bg-center transition-transform duration-[15s] group-hover:scale-110 opacity-70 group-hover:opacity-100"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 to-transparent"></div>
            
            <div className="relative z-10 w-full h-full flex flex-col justify-end p-8 md:p-14">
              <p className="text-[#FF6B00] font-black tracking-[0.2em] text-xs uppercase mb-3 drop-shadow-md">Rápido y Seguro</p>
              <h2 className="text-4xl md:text-5xl font-black italic uppercase leading-[0.9] mb-4">Protección<br/>Total.</h2>
              <p className="text-gray-300 text-sm md:text-base font-medium mb-8 max-w-sm">
                No arriesgues tu dinero. Pagos seguros por Pago Móvil o Binance y envíos a todo nivel nacional al instante.
              </p>
              <div>
                <button 
                  onClick={() => navigate('/catalogo')}
                  className="bg-white text-black font-black rounded-full px-8 py-3 uppercase tracking-widest hover:scale-105 transition-transform text-sm shadow-xl"
                >
                  Ir de Compras
                </button>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 
        ======================================================================
        4. SECCIÓN DE OFERTAS (Solo si hay ofertas)
        ======================================================================
      */}
      {offerProducts.length > 0 && (
        <section className="py-16 px-6 md:px-16 max-w-[1920px] mx-auto bg-gradient-to-b from-black to-[#1a0a0a]">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-10 gap-4">
            <div>
              <div className="bg-[#FF003C] text-white text-[10px] font-black uppercase px-3 py-1 rounded-full mb-3 inline-block animate-pulse">
                Lo mejor del precio
              </div>
              <h2 className="text-3xl md:text-5xl font-black italic uppercase tracking-tighter">
                Nuestras <span className="text-[#FF003C]">Ofertas</span>
              </h2>
            </div>
            <button 
              onClick={() => navigate('/catalogo?cat=oferta')}
              className="text-[#FF003C] hover:text-white text-sm font-black uppercase tracking-widest transition-colors flex items-center gap-2 group"
            >
              Ver Todas
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {offerProducts.map(producto => (
              <div 
                key={producto.id} 
                className="group cursor-pointer flex flex-col bg-[#111] p-2 rounded-2xl border border-[#FF003C]/20 hover:border-[#FF003C]/50 transition-all"
                onClick={() => navigate(`/catalogo?prodId=${producto.id}`)}
              >
                <div className="w-full aspect-[4/5] bg-black mb-4 overflow-hidden relative rounded-xl">
                  <img 
                    src={producto.images && producto.images[0] ? (producto.images[0].startsWith('http') ? producto.images[0] : `${BACKEND_URL}${producto.images[0]}`) : '/placeholder.png'} 
                    alt={producto.descripcion} 
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <div className="absolute top-2 right-2 bg-[#FF003C] text-white text-[10px] font-black uppercase px-2 py-1 rounded-md shadow-lg">
                    OFERTA
                  </div>
                </div>
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-300 leading-snug line-clamp-2 h-8 group-hover:text-white transition-colors text-center">
                  {producto.descripcion}
                </h3>
                <div className="flex flex-col items-center mt-2">
                  <span className="text-[#39FF14] font-black text-sm">${(producto.precio_oferta || producto.precio).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 
        ======================================================================
        4.1 SECCIÓN DE DESCUENTOS (Solo si hay descuentos > 0)
        ======================================================================
      */}
      {discountProducts.length > 0 && (
        <section className="py-16 px-6 md:px-16 max-w-[1920px] mx-auto">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-10 gap-4">
            <div>
              <div className="bg-[#39FF14] text-black text-[10px] font-black uppercase px-3 py-1 rounded-full mb-3 inline-block">
                Porcentaje de Ahorro
              </div>
              <h2 className="text-3xl md:text-5xl font-black italic uppercase tracking-tighter">
                Super <span className="text-[#39FF14]">Descuentos</span>
              </h2>
            </div>
            <button 
              onClick={() => navigate('/catalogo?cat=descuento')}
              className="text-[#39FF14] hover:text-white text-sm font-black uppercase tracking-widest transition-colors flex items-center gap-2 group"
            >
              Ver Todo %
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {discountProducts.map(producto => (
              <div 
                key={producto.id} 
                className="group cursor-pointer flex flex-col bg-[#111] p-2 rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14]/50 transition-all"
                onClick={() => navigate(`/catalogo?prodId=${producto.id}`)}
              >
                <div className="w-full aspect-[4/5] bg-black mb-4 overflow-hidden relative rounded-xl">
                  <img 
                    src={producto.images && producto.images[0] ? (producto.images[0].startsWith('http') ? producto.images[0] : `${BACKEND_URL}${producto.images[0]}`) : '/placeholder.png'} 
                    alt={producto.descripcion} 
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <div className="absolute top-2 right-2 bg-[#FF003C] text-white text-[10px] font-black uppercase px-2 py-1 rounded-md shadow-lg">
                    -{producto.descuento}%
                  </div>
                </div>
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-300 leading-snug line-clamp-2 h-8 group-hover:text-white transition-colors text-center">
                  {producto.descripcion}
                </h3>
                <div className="flex flex-col items-center mt-2">
                  <span className="text-[9px] text-gray-500 line-through font-bold">${(producto.precio || 0).toFixed(2)}</span>
                  <span className="text-[#39FF14] font-black text-sm">
                    ${(producto.precio_oferta || (producto.precio * (1 - producto.descuento/100))).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 
        ======================================================================
        5. CAROUSEL/GRID "LO MÁS NUEVO"
        ======================================================================
      */}
      {popularProducts.length > 0 && (
        <section className="pb-24 px-6 md:px-16 max-w-[1920px] mx-auto">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-10 gap-4">
            <h2 className="text-3xl md:text-4xl font-black italic uppercase tracking-wider">
              Novedades
            </h2>
            <button 
              onClick={() => navigate('/catalogo')}
              className="text-white hover:text-[#39FF14] text-sm font-bold uppercase tracking-wider transition-colors flex items-center gap-2 group"
            >
              Comprar Novedades
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {popularProducts.map(producto => (
              <div 
                key={producto.id} 
                className="group cursor-pointer flex flex-col"
                onClick={() => navigate(`/catalogo?prodId=${producto.id}`)}
              >
                <div className="w-full aspect-[4/5] bg-[#111] mb-4 overflow-hidden relative">
                  <img 
                    src={producto.images && producto.images[0] ? `${BACKEND_URL}${producto.images[0]}` : '/placeholder.png'} 
                    alt={producto.descripcion} 
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  {producto.oferta && (
                    <div className="absolute top-2 left-2 bg-gradient-to-r from-[#FF003C] to-[#FF6B00] text-white text-[9px] font-black uppercase px-2 py-1 rounded-sm">
                      Oferta
                    </div>
                  )}
                </div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300 leading-snug line-clamp-2 h-8 group-hover:text-white transition-colors">
                  {producto.descripcion}
                </h3>
                <p className="text-white font-medium mt-1 text-sm">
                  ${(producto.precio || 0).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* 
        ======================================================================
        5. SECCIÓN PERSONAL: FARMASI
        ======================================================================
      */}
      <section className="w-full mt-10 mb-10 px-4 md:px-16 max-w-[1920px] mx-auto">
        <div className="relative w-full rounded-3xl overflow-hidden border border-gray-800 bg-[#111] flex flex-col lg:flex-row items-center group">
          
          {/* Mitad Izquierda: Contenido y Botones */}
          <div className="relative z-10 w-full lg:w-1/2 p-8 md:p-16 flex flex-col items-start text-left">
            <div className="absolute inset-0 bg-gradient-to-br from-[#ff0050]/5 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
            
            {/* Logo/Texto Farmasi */}
            <h2 className="text-4xl md:text-6xl font-black tracking-widest uppercase mb-4 text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">
              FARMASI
            </h2>
            <div className="h-1 w-20 bg-gradient-to-r from-[#ff0050] to-[#39FF14] mb-6"></div>
            
            <p className="text-gray-300 text-sm md:text-lg mb-10 max-w-lg font-medium relative z-10">
              Descubre nuestra línea de cuidado personal, nutrición y belleza con productos de calidad europea. 
              Únete a mi equipo o adquiere tus productos directamente desde mi tienda oficial.
            </p>

            <div className="flex flex-col gap-4 w-full sm:w-auto relative z-10">
              {/* Botón Comprar */}
              <a 
                href="https://www.farmasius.com/andresgbarreto"
                target="_blank"
                rel="noreferrer"
                className="w-full bg-white text-black font-black uppercase tracking-widest text-xs md:text-sm px-8 py-4 rounded-full hover:bg-gray-200 transition-all hover:scale-105 shadow-[0_5px_20px_rgba(255,255,255,0.2)] flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
                Aquí puedes comprar en Farmasi
              </a>

              {/* Botón Afiliarse (WhatsApp) */}
              <a 
                href="https://wa.me/584129317277?text=Hola,%20me%20gustar%C3%ADa%20afiliarme%20a%20Farmasi"
                target="_blank"
                rel="noreferrer"
                className="w-full bg-transparent border-2 border-[#39FF14] text-[#39FF14] font-black uppercase tracking-widest text-xs md:text-sm px-8 py-4 rounded-full hover:bg-[#39FF14] hover:text-black transition-all hover:scale-105 shadow-[0_0_15px_rgba(57,255,20,0.3)] flex items-center justify-center gap-2 text-center"
              >
                <MessageCircle className="w-5 h-5 flex-shrink-0" />
                ¿Te gustaría afiliarte? Presiona acá
              </a>
            </div>
          </div>

          {/* Mitad Derecha: Imagen de Productos/Logo */}
          <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-[500px] bg-white relative overflow-hidden flex items-center justify-center">
            {/* 
              Aquí va la imagen que el usuario proporcione. 
              Por defecto busca 'farmasi-banner.jpg' en la carpeta public.
            */}
            <img 
              src="/farmasi-banner.jpg" 
              alt="Productos Farmasi" 
              className="w-full h-full object-cover transition-transform duration-[10s] group-hover:scale-105"
              onError={(e) => {
                // Fallback a una imagen de belleza elegante de Unsplash si no se encuentra la local
                e.target.src = "https://images.unsplash.com/photo-1596462502278-27bfdc403348?q=80&w=1600";
              }}
            />
            
            {/* Overlay sutil para integrar la imagen blanca con el diseño oscuro */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#111] via-transparent to-transparent lg:w-1/4"></div>
          </div>

        </div>
      </section>

      {/* Footer Oficial */}
      <Footer />
    </div>
  );
}
